interface LoadingOverlayProps {
  message?: string
}

export function LoadingOverlay({ message = 'Loading...' }: LoadingOverlayProps) {
  return (
    <div className="loading-overlay">
      {/* Backdrop with blur effect */}
      <div className="loading-backdrop" />

      {/* Content container */}
      <div className="loading-content">
        {/* Modern spinner */}
        <div className="loading-spinner-container">
          <div className="loading-spinner">
            <div className="spinner-ring spinner-ring-1"></div>
            <div className="spinner-ring spinner-ring-2"></div>
            <div className="spinner-ring spinner-ring-3"></div>
            <div className="spinner-dot"></div>
          </div>
        </div>

        {/* Message */}
        <div className="loading-message">{message}</div>
      </div>

      <style>{`
        /* Overlay container */
        .loading-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: fadeIn 0.2s ease-out;
        }

        /* Backdrop with blur */
        .loading-backdrop {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.75);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
        }

        /* Content container */
        .loading-content {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
          padding: 32px 48px;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 16px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25),
                      0 0 0 1px rgba(255, 255, 255, 0.1);
          animation: scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        /* Dark theme support */
        @media (prefers-color-scheme: dark) {
          .loading-content {
            background: rgba(30, 41, 59, 0.95);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5),
                        0 0 0 1px rgba(255, 255, 255, 0.05);
          }
        }

        /* Spinner container */
        .loading-spinner-container {
          position: relative;
          width: 80px;
          height: 80px;
        }

        /* Spinner */
        .loading-spinner {
          position: relative;
          width: 100%;
          height: 100%;
        }

        /* Spinner rings */
        .spinner-ring {
          position: absolute;
          inset: 0;
          border: 3px solid transparent;
          border-radius: 50%;
          animation: spinRing 2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
        }

        .spinner-ring-1 {
          border-top-color: #3b82f6;
          animation-delay: 0s;
        }

        .spinner-ring-2 {
          border-top-color: #60a5fa;
          animation-delay: -0.3s;
          opacity: 0.7;
        }

        .spinner-ring-3 {
          border-top-color: #93c5fd;
          animation-delay: -0.6s;
          opacity: 0.4;
        }

        /* Center dot pulse */
        .spinner-dot {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 16px;
          height: 16px;
          background: linear-gradient(135deg, #3b82f6, #60a5fa);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          animation: pulseDot 1.5s ease-in-out infinite;
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.5);
        }

        /* Message text */
        .loading-message {
          font-size: 16px;
          font-weight: 500;
          color: #1f2937;
          letter-spacing: 0.01em;
          animation: fadeInUp 0.4s ease-out 0.1s both;
        }

        @media (prefers-color-scheme: dark) {
          .loading-message {
            color: #f3f4f6;
          }
        }

        /* Animations */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes spinRing {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @keyframes pulseDot {
          0%, 100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.3);
            opacity: 0.7;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Mobile responsive */
        @media (max-width: 640px) {
          .loading-content {
            padding: 24px 32px;
            gap: 20px;
          }

          .loading-spinner-container {
            width: 64px;
            height: 64px;
          }

          .loading-message {
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  )
}
